// DashboardEditor — the surface shown when a dashboard is selected in
// the sidebar. It toggles between two full-pane modes:
//
//   - "render"    — a live render of the dashboard's React source.
//   - "configure" — a Monaco editor for that source.
//
// The header flips the mode (Configure ⇄ View Render) and a Save button
// persists the current source to the `dashboards` table via the
// updateDashboardCode mutation. The server is the source of truth: code
// is seeded from `dashboard.code` and re-seeded when the selected
// dashboard changes. A dashboard with no saved code yet falls back to an
// empty starter component.

import { useEffect, useRef, useState } from 'react'
import {
  Check,
  Eye,
  FolderInput,
  Loader2,
  MoreHorizontal,
  Pencil,
  Save,
  Settings2,
  Trash2
} from 'lucide-react'
import {
  useUpdateDashboardCodeMutation,
  type Dashboard
} from '@axilon-apps/domains-ontology-ui-state'

import { ResourceShareButton } from '../../../components/ResourceSharingDialog'
import { Button } from '../../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../components/ui/dropdown-menu'
import CodeEditorPane, { type MonacoCodeEditor } from './CodeEditorPane'
import type { EditorMode } from './editor-mode'
import InsertTagButton from './InsertTagButton'
import SandboxFrame from '../../../components/DashboardSandbox/SandboxFrame'
import { useSandboxTemplateRegistry } from '../../../components/DashboardSandbox/useSandboxTemplateRegistry'
import { STARTER_CODE } from './template'

/** Seed value for the editor: saved code, or the starter when empty. */
function initialCode(dashboard: Dashboard): string {
  return dashboard.code !== '' ? dashboard.code : STARTER_CODE
}

export interface DashboardEditorProps {
  dashboard: Dashboard
  /** Owned by the page so it can offer the mode as chat context. */
  mode: EditorMode
  onModeChange: (mode: EditorMode) => void
  onRename?: () => void
  onMove?: () => void
  onDelete?: () => void
}

const DashboardEditor = ({
  dashboard,
  mode,
  onModeChange,
  onRename,
  onMove,
  onDelete
}: DashboardEditorProps) => {
  const [code, setCode] = useState(() => initialCode(dashboard))
  // Last value known to be persisted server-side, so we can show a dirty
  // state and disable Save when there's nothing to save.
  const [savedCode, setSavedCode] = useState(code)
  // Bumped to remount the sandbox iframe when persisted code changes so
  // the render pane can't show a stale compile from a prior payload.
  const [renderEpoch, setRenderEpoch] = useState(0)

  const [updateCode, { isLoading: isSaving, isError: saveFailed }] =
    useUpdateDashboardCodeMutation()

  // Mounted Monaco instance (configure mode only) for cursor-aware inserts
  // from the tag picker. Cleared when the pane unmounts on a mode flip.
  const editorRef = useRef<MonacoCodeEditor | null>(null)

  // Insert the graph node ID as a quoted literal for the node-ID hooks.
  // Provider paths resolve from registry metadata when the hook reads it.
  const insertTag = (nodeId: string) => {
    const literal = JSON.stringify(nodeId)
    const editor = editorRef.current
    if (editor) {
      const selection = editor.getSelection()
      if (selection) {
        editor.executeEdits('tag-picker', [
          { range: selection, text: literal, forceMoveMarkers: true }
        ])
        editor.focus()
        return
      }
    }
    setCode(
      (prev) => `${prev}\n// TODO: wire into useTagValues({ nodeIds: [...] })\n// ${literal}\n`
    )
  }

  // Forward saved templates into the sandbox so the dashboard's TSX
  // can compose them with `<Template path="..." />`. Empty registry
  // when the call is loading or templates aren't yet available.
  const templateRegistry = useSandboxTemplateRegistry()

  // Re-hydrate (and drop back to render mode) when the user switches to a
  // different dashboard.
  useEffect(() => {
    const next = initialCode(dashboard)
    setCode(next)
    setSavedCode(next)
    setRenderEpoch((epoch) => epoch + 1)
    onModeChange('render')
    // Only re-seed on a genuine dashboard switch — not when the list
    // refetches and hands us a new object for the same row (which would
    // clobber unsaved edits).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard.id])

  // When the server-side code changes (chat agent edit, another tab, RTK
  // refetch) and the editor is clean, adopt the new source and reload the
  // sandbox iframe. Skip while dirty so a list refetch can't clobber edits.
  useEffect(() => {
    if (code !== savedCode) return
    const next = initialCode(dashboard)
    if (next === code) return
    setCode(next)
    setSavedCode(next)
    setRenderEpoch((epoch) => epoch + 1)
  }, [dashboard.code, dashboard.id, code, savedCode])

  const isDirty = code !== savedCode

  const handleSave = async () => {
    try {
      await updateCode({ id: dashboard.id, input: { code } }).unwrap()
      setSavedCode(code)
      setRenderEpoch((epoch) => epoch + 1)
    } catch {
      // Surfaced via the saveFailed flag below; nothing else to do here.
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col" data-tour-id="dashboard-editor">
      <div
        data-tour-id="dashboard-editor-header"
        className="flex items-center gap-3 border-b border-line px-6 py-3"
      >
        <h1 className="m-0 text-lg font-semibold text-ink">{dashboard.name}</h1>
        <code className="rounded bg-bg-sunk px-2 py-0.5 font-mono text-11 text-ink-3">
          /{dashboard.path}
        </code>
        <div className="flex-1" />

        {mode === 'configure' ? <InsertTagButton onInsert={insertTag} /> : null}

        {saveFailed ? (
          <span className="text-11 text-st-down">Couldn’t save</span>
        ) : isDirty ? (
          <span className="text-11 text-ink-3">Unsaved changes</span>
        ) : (
          <span className="flex items-center gap-1 text-11 text-ink-3">
            <Check size={12} aria-hidden />
            Saved
          </span>
        )}

        <Button size="sm" onClick={handleSave} disabled={isSaving || !isDirty}>
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save
        </Button>

        {dashboard.accessScopeId ? (
          <ResourceShareButton
            resourceName={dashboard.name}
            resourceLabel="dashboard"
            accessScopeId={dashboard.accessScopeId}
          />
        ) : null}

        {onRename || onMove || onDelete ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon-sm"
                aria-label="Dashboard actions"
                data-testid="dashboard-editor-overflow"
              >
                <MoreHorizontal size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onRename ? (
                <DropdownMenuItem onSelect={onRename}>
                  <Pencil />
                  Rename
                </DropdownMenuItem>
              ) : null}
              {onMove ? (
                <DropdownMenuItem onSelect={onMove}>
                  <FolderInput />
                  Move to…
                </DropdownMenuItem>
              ) : null}
              {onDelete ? (
                <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                  <Trash2 />
                  Delete
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {mode === 'render' ? (
          <Button variant="outline" size="sm" onClick={() => onModeChange('configure')}>
            <Settings2 size={14} />
            Configure
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => onModeChange('render')}>
            <Eye size={14} />
            View Render
          </Button>
        )}
      </div>

      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {mode === 'render' ? (
          <SandboxFrame
            key={renderEpoch}
            code={code}
            templates={templateRegistry}
            dashboardId={dashboard.id}
          />
        ) : (
          <CodeEditorPane
            code={code}
            onChange={setCode}
            onEditorMount={(editor) => {
              editorRef.current = editor
            }}
          />
        )}
      </div>
    </div>
  )
}

export default DashboardEditor
